'use client'

import { use, useState } from 'react'
import Link from 'next/link'
import { useAuth } from '@/contexts/AuthContext'
import { usePortfolio } from '@/contexts/PortfolioContext'
import { TierGate } from '@/components/app'

// Sample lot data - in production this comes from API
const lotsData: Record<string, {
  id: string
  title: string
  artist: string
  artistSlug: string
  year: string
  medium: string
  dimensions: string
  edition?: string
  // Sale info
  house: string
  saleTitle: string
  saleDate: string
  lotNumber: string
  // Pricing
  estimateLow: number
  estimateHigh: number
  hammer: number
  premium: number
  currency: string
  // Classification
  period: string
  movement: string
  category: string
  // Provenance
  provenance: { owner: string; period: string; acquired?: string }[]
  // Exhibition history
  exhibitions: { title: string; venue: string; year: string }[]
  // Literature
  literature: string[]
  // Image placeholder
  imageUrl?: string
  // Condition
  condition?: string
}> = {
  'CHR-2024-1847': {
    id: 'CHR-2024-1847',
    title: 'Untitled (Devil)',
    artist: 'Jean-Michel Basquiat',
    artistSlug: 'jean-michel-basquiat',
    year: '1982',
    medium: 'Acrylic, oilstick and spray paint on canvas',
    dimensions: '238.8 x 500.4 cm (94 x 197 in.)',
    house: "Christie's",
    saleTitle: '21st Century Evening Sale',
    saleDate: 'February 8, 2024',
    lotNumber: '12A',
    estimateLow: 35000000,
    estimateHigh: 45000000,
    hammer: 42400000,
    premium: 18.2,
    currency: 'USD',
    period: 'Contemporary',
    movement: 'Neo-Expressionism',
    category: 'Paintings',
    provenance: [
      { owner: 'Private Collection, New York', period: '2010-2024', acquired: "Sotheby's New York, 2010" },
      { owner: 'The Mugrabi Collection', period: '1998-2010' },
      { owner: 'Larry Gagosian, New York', period: '1988-1998' },
      { owner: 'Acquired directly from the artist', period: '1982-1988' },
    ],
    exhibitions: [
      { title: 'Basquiat: Boom for Real', venue: 'Barbican Centre, London', year: '2017' },
      { title: 'Jean-Michel Basquiat', venue: 'Brooklyn Museum', year: '2005' },
      { title: 'Jean-Michel Basquiat: Paintings 1981-1984', venue: 'Fruitmarket Gallery, Edinburgh', year: '1984' },
    ],
    literature: [
      'R. Marshall, Jean-Michel Basquiat, New York, 1992, p. 142, illustrated in color',
      'R. D. Marshall and J.-L. Prat, Jean-Michel Basquiat, Paris, 1996, vol. II, p. 88, no. 4, illustrated in color',
      'The Estate of Jean-Michel Basquiat, New York, 2000, p. 156',
    ],
    condition: 'Excellent. Minor surface dust. No restoration.',
  },
  'SOT-2024-3291': {
    id: 'SOT-2024-3291',
    title: 'Infinity Nets (TWHOQ)',
    artist: 'Yayoi Kusama',
    artistSlug: 'yayoi-kusama',
    year: '2006',
    medium: 'Acrylic on canvas',
    dimensions: '194 x 259 cm (76.4 x 102 in.)',
    house: "Sotheby's",
    saleTitle: 'Contemporary Art Evening Auction',
    saleDate: 'February 5, 2024',
    lotNumber: '28',
    estimateLow: 6000000,
    estimateHigh: 8000000,
    hammer: 8900000,
    premium: 12.4,
    currency: 'USD',
    period: 'Contemporary',
    movement: 'Pop Art',
    category: 'Paintings',
    provenance: [
      { owner: 'Private Collection, Hong Kong', period: '2018-2024', acquired: 'Phillips Hong Kong, 2018' },
      { owner: 'Private Collection, Tokyo', period: '2006-2018', acquired: 'Acquired directly from the artist' },
    ],
    exhibitions: [
      { title: 'Yayoi Kusama: Infinity Mirrors', venue: 'Hirshhorn Museum, Washington D.C.', year: '2017' },
      { title: 'Yayoi Kusama', venue: 'Tate Modern, London', year: '2012' },
    ],
    literature: [
      'Yayoi Kusama: Infinity Net, The Autobiography of Yayoi Kusama, London, 2011, p. 89',
    ],
    condition: 'Very good. Minor wear to edges consistent with age.',
  },
  'PHL-2024-0892': {
    id: 'PHL-2024-0892',
    title: 'Abstraktes Bild (648-3)',
    artist: 'Gerhard Richter',
    artistSlug: 'gerhard-richter',
    year: '1987',
    medium: 'Oil on canvas',
    dimensions: '200.7 x 200.7 cm (79 x 79 in.)',
    house: 'Phillips',
    saleTitle: '20th Century & Contemporary Art Evening Sale',
    saleDate: 'February 1, 2024',
    lotNumber: '8',
    estimateLow: 15000000,
    estimateHigh: 20000000,
    hammer: 18200000,
    premium: 14.1,
    currency: 'USD',
    period: 'Contemporary',
    movement: 'Abstract Expressionism',
    category: 'Paintings',
    provenance: [
      { owner: 'Private Collection, Switzerland', period: '2005-2024' },
      { owner: 'Marian Goodman Gallery, New York', period: '1988-2005' },
      { owner: 'Acquired directly from the artist', period: '1987-1988' },
    ],
    exhibitions: [
      { title: 'Gerhard Richter: Panorama', venue: 'Tate Modern, London', year: '2011' },
      { title: 'Gerhard Richter: 40 Years of Painting', venue: 'MoMA, New York', year: '2002' },
    ],
    literature: [
      'D. Elger and H. U. Obrist, Gerhard Richter: A Life in Painting, Chicago, 2009, p. 312',
      'Gerhard Richter: Catalogue Raisonné 1962-1993, vol. III, Ostfildern-Ruit, 1993, no. 648-3',
    ],
    condition: 'Excellent. Original stretcher. No restoration.',
  },
  'CHR-2024-1901': {
    id: 'CHR-2024-1901',
    title: 'Green Beret',
    artist: 'Amoako Boafo',
    artistSlug: 'amoako-boafo',
    year: '2020',
    medium: 'Oil on canvas',
    dimensions: '210 x 180 cm (82.7 x 70.9 in.)',
    house: "Christie's",
    saleTitle: '21st Century Evening Sale',
    saleDate: 'January 28, 2024',
    lotNumber: '56',
    estimateLow: 800000,
    estimateHigh: 1200000,
    hammer: 3400000,
    premium: 24.5,
    currency: 'USD',
    period: 'Contemporary',
    movement: 'African Contemporary',
    category: 'Paintings',
    provenance: [
      { owner: 'Private Collection, Los Angeles', period: '2020-2024', acquired: 'Roberts Projects, Los Angeles' },
    ],
    exhibitions: [
      { title: 'Amoako Boafo: Soul of Black Folks', venue: 'Roberts Projects, Los Angeles', year: '2020' },
    ],
    literature: [],
    condition: 'Excellent. As new.',
  },
  'SOT-2024-3187': {
    id: 'SOT-2024-3187',
    title: 'Girl with Balloon',
    artist: 'Banksy',
    artistSlug: 'banksy',
    year: '2006',
    medium: 'Spray paint and acrylic on canvas',
    dimensions: '101 x 78 cm (39.8 x 30.7 in.)',
    edition: 'Unique',
    house: "Sotheby's",
    saleTitle: 'Contemporary Art Evening Auction',
    saleDate: 'January 20, 2024',
    lotNumber: '15',
    estimateLow: 20000000,
    estimateHigh: 30000000,
    hammer: 25000000,
    premium: 8.9,
    currency: 'USD',
    period: 'Contemporary',
    movement: 'Street Art',
    category: 'Paintings',
    provenance: [
      { owner: 'Private European Collection', period: '2018-2024' },
      { owner: 'Acquired at Sothebys London, 2018 (the famous shredding incident)', period: '2018' },
      { owner: 'Private Collection, UK', period: '2006-2018' },
    ],
    exhibitions: [
      { title: 'The Art of Banksy', venue: 'Various venues worldwide', year: '2019-2023' },
    ],
    literature: [
      'Banksy: Wall and Piece, London, 2005, p. 174',
    ],
    condition: 'Lower portion intentionally shredded by the artist. Renamed "Love is in the Bin" after 2018 sale.',
  },
}

// Simple ID aliases for easier navigation
const lotAliases: Record<string, string> = {
  'lot-1': 'CHR-2024-1847',
  'lot-2': 'SOT-2024-3291',
  'lot-3': 'PHL-2024-0892',
  'lot-4': 'CHR-2024-1901',
  'lot-5': 'SOT-2024-3187',
}

function formatCurrency(amount: number, currency: string = 'USD'): string {
  if (amount >= 1000000) {
    return `$${(amount / 1000000).toFixed(1)}M`
  }
  if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(0)}K`
  }
  return `$${amount.toLocaleString()}`
}

function formatFullCurrency(amount: number): string {
  return `$${amount.toLocaleString()}`
}

function ShareButton({ title }: { title?: string }) {
  const [copied, setCopied] = useState(false)

  const handleShare = async () => {
    const shareUrl = window.location.href
    const shareTitle = title || 'Lot - salle.art'

    if (navigator.share) {
      try {
        await navigator.share({ title: shareTitle, url: shareUrl })
      } catch (err) {}
    } else {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <button
      onClick={handleShare}
      className="pill text-sm flex items-center gap-2"
      style={{ background: 'transparent', border: '1px solid var(--border-standard)', color: 'var(--text-primary)' }}
    >
      {copied ? (
        <>
          <svg className="w-4 h-4 text-positive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
          </svg>
          Copied!
        </>
      ) : (
        <>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
          </svg>
          Share
        </>
      )}
    </button>
  )
}

export default function LotDetailPage({ params }: { params: Promise<{ id: string }> }) {
  const { id } = use(params)
  const { user, canExport } = useAuth()
  const { addItem, removeItem, isInPortfolio } = usePortfolio()
  const [showFullProvenance, setShowFullProvenance] = useState(false)

  // Resolve alias to actual ID
  const resolvedId = lotAliases[id] || id
  const lot = lotsData[resolvedId]
  const inPortfolio = lot ? isInPortfolio(lot.id) : false

  const handlePortfolioToggle = () => {
    if (!lot) return
    if (inPortfolio) {
      removeItem(lot.id)
    } else {
      addItem({
        lotId: lot.id,
        title: lot.title,
        artist: lot.artist,
        artistSlug: lot.artistSlug,
        addedPrice: lot.hammer,
        currentPrice: lot.hammer * 1.05, // Simulate 5% appreciation
        medium: lot.medium,
        year: lot.year,
        house: lot.house,
        saleDate: lot.saleDate,
      })
    }
  }

  if (!lot) {
    return (
      <div className="px-4 md:px-6 py-6">
        <div className="text-center py-12">
          <h1 className="font-display text-2xl font-light mb-4">Lot not found</h1>
          <p className="text-text-secondary mb-6">The lot you're looking for doesn't exist or has been removed.</p>
          <Link href="/app/market" className="pill" style={{ background: 'var(--accent-primary)', color: 'white' }}>
            Browse Market
          </Link>
        </div>
      </div>
    )
  }

  const hammerVsEstimate = lot.hammer > lot.estimateHigh
    ? { text: 'Above estimate', color: 'var(--positive)', pct: ((lot.hammer - lot.estimateHigh) / lot.estimateHigh * 100).toFixed(0) }
    : lot.hammer < lot.estimateLow
    ? { text: 'Below estimate', color: 'var(--negative)', pct: ((lot.estimateLow - lot.hammer) / lot.estimateLow * 100).toFixed(0) }
    : { text: 'Within estimate', color: 'var(--text-secondary)', pct: null }

  const totalPrice = lot.hammer * (1 + lot.premium / 100)

  return (
    <div className="px-4 md:px-6 py-6 max-w-5xl mx-auto">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-text-tertiary mb-6">
        <Link href="/app/market" className="hover:text-text-secondary transition-colors">Market</Link>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <Link href={`/app/artists/${lot.artistSlug}`} className="hover:text-text-secondary transition-colors">
          {lot.artist}
        </Link>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <span className="text-text-primary">{lot.title}</span>
      </div>

      {/* Header */}
      <div className="flex flex-col lg:flex-row gap-8 mb-8">
        {/* Image placeholder */}
        <div className="lg:w-1/2">
          <div className="aspect-[4/3] bg-page rounded-2xl flex items-center justify-center border border-border-subtle">
            <div className="text-center p-8">
              <svg className="w-16 h-16 mx-auto text-text-muted mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-text-tertiary text-sm">Image available in full catalog</p>
            </div>
          </div>
        </div>

        {/* Details */}
        <div className="lg:w-1/2">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h1 className="font-display text-2xl md:text-3xl font-light mb-2">{lot.title}</h1>
              <Link
                href={`/app/artists/${lot.artistSlug}`}
                className="text-lg text-accent-primary hover:underline"
              >
                {lot.artist}
              </Link>
              <p className="text-text-secondary mt-1">{lot.year}</p>
            </div>
            <ShareButton title={`${lot.artist} - ${lot.title} | salle.art`} />
          </div>

          {/* Medium & dimensions */}
          <div className="space-y-2 mb-6 text-sm">
            <p className="text-text-secondary">{lot.medium}</p>
            <p className="text-text-tertiary">{lot.dimensions}</p>
            {lot.edition && <p className="text-text-tertiary">Edition: {lot.edition}</p>}
          </div>

          {/* Classification badges */}
          <div className="flex flex-wrap gap-2 mb-6">
            <span className="text-xs px-3 py-1 rounded-full border border-border text-text-secondary">
              {lot.period}
            </span>
            <span className="text-xs px-3 py-1 rounded-full border border-border text-text-secondary">
              {lot.movement}
            </span>
            <span className="text-xs px-3 py-1 rounded-full border border-border text-text-secondary">
              {lot.category}
            </span>
          </div>

          {/* Sale info */}
          <div className="card p-5 mb-4">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-sm font-medium">{lot.house}</span>
              <span className="text-text-tertiary">•</span>
              <span className="text-sm text-text-secondary">{lot.saleTitle}</span>
            </div>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-3xl font-display font-light">{formatCurrency(lot.hammer)}</span>
              <span className="text-text-tertiary text-sm">hammer</span>
            </div>
            <div className="flex items-center gap-3 text-sm mb-4">
              <span style={{ color: hammerVsEstimate.color }}>
                {hammerVsEstimate.text}
                {hammerVsEstimate.pct && ` (+${hammerVsEstimate.pct}%)`}
              </span>
              <span className="text-text-tertiary">
                Est. {formatCurrency(lot.estimateLow)}–{formatCurrency(lot.estimateHigh)}
              </span>
            </div>
            <div className="pt-4 border-t border-border-subtle">
              <div className="flex items-center justify-between text-sm">
                <span className="text-text-secondary">Total with premium ({lot.premium}%)</span>
                <span className="font-medium">{formatFullCurrency(Math.round(totalPrice))}</span>
              </div>
            </div>
          </div>

          {/* Sale details */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-text-tertiary mb-1">Sale Date</p>
              <p className="font-medium">{lot.saleDate}</p>
            </div>
            <div>
              <p className="text-text-tertiary mb-1">Lot Number</p>
              <p className="font-medium">{lot.lotNumber}</p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-3 mt-6">
            <button
              onClick={handlePortfolioToggle}
              className="pill text-sm flex items-center gap-2"
              style={{
                background: inPortfolio ? 'var(--positive)' : 'var(--accent-primary)',
                color: 'white'
              }}
            >
              {inPortfolio ? (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
                  </svg>
                  In Portfolio
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
                  </svg>
                  Add to Portfolio
                </>
              )}
            </button>
            <button
              className="pill text-sm flex items-center gap-2"
              style={{ background: 'transparent', border: '1px solid var(--border-standard)', color: 'var(--text-primary)' }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Find Comparables
            </button>
          </div>
        </div>
      </div>

      {/* Provenance */}
      {lot.provenance.length > 0 && (
        <div className="card p-6 mb-6">
          <h2 className="font-medium mb-4 flex items-center gap-2">
            <svg className="w-5 h-5 text-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            Provenance
          </h2>
          <div className="space-y-3">
            {(showFullProvenance ? lot.provenance : lot.provenance.slice(0, 2)).map((p, i) => (
              <div key={i} className="flex items-start gap-4 py-2 border-b border-border-subtle last:border-0">
                <div className="w-2 h-2 rounded-full bg-accent-primary mt-2 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium">{p.owner}</p>
                  <p className="text-xs text-text-tertiary">{p.period}</p>
                  {p.acquired && <p className="text-xs text-text-tertiary mt-1">{p.acquired}</p>}
                </div>
              </div>
            ))}
          </div>
          {lot.provenance.length > 2 && (
            <button
              onClick={() => setShowFullProvenance(!showFullProvenance)}
              className="text-sm text-accent-primary hover:underline mt-4"
            >
              {showFullProvenance ? 'Show less' : `Show ${lot.provenance.length - 2} more`}
            </button>
          )}
        </div>
      )}

      {/* Exhibition History */}
      {lot.exhibitions.length > 0 && (
        <div className="card p-6 mb-6">
          <h2 className="font-medium mb-4 flex items-center gap-2">
            <svg className="w-5 h-5 text-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
            </svg>
            Exhibition History
          </h2>
          <div className="space-y-3">
            {lot.exhibitions.map((ex, i) => (
              <div key={i} className="flex items-start justify-between py-2 border-b border-border-subtle last:border-0">
                <div>
                  <p className="text-sm font-medium">{ex.title}</p>
                  <p className="text-xs text-text-tertiary">{ex.venue}</p>
                </div>
                <span className="text-sm text-text-tertiary">{ex.year}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Literature */}
      {lot.literature.length > 0 && (
        <TierGate feature="5-year-history">
          <div className="card p-6 mb-6">
            <h2 className="font-medium mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              Literature
            </h2>
            <ul className="space-y-2">
              {lot.literature.map((lit, i) => (
                <li key={i} className="text-sm text-text-secondary leading-relaxed">
                  {lit}
                </li>
              ))}
            </ul>
          </div>
        </TierGate>
      )}

      {/* Condition */}
      {lot.condition && (
        <div className="card p-6 mb-6">
          <h2 className="font-medium mb-2 flex items-center gap-2">
            <svg className="w-5 h-5 text-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Condition
          </h2>
          <p className="text-sm text-text-secondary">{lot.condition}</p>
        </div>
      )}

      {/* Similar Lots */}
      <div className="card p-6 mb-6">
        <h2 className="font-medium mb-4 flex items-center gap-2">
          <svg className="w-5 h-5 text-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
          </svg>
          Similar Lots
        </h2>
        <div className="grid md:grid-cols-3 gap-4">
          {Object.values(lotsData)
            .filter(l => l.id !== lot.id && l.movement === lot.movement)
            .slice(0, 3)
            .map((similarLot) => (
              <Link
                key={similarLot.id}
                href={`/app/lots/${similarLot.id}`}
                className="p-4 rounded-xl bg-page hover:bg-card transition-colors"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-text-tertiary">{similarLot.house}</span>
                  <span className="text-xs text-text-tertiary">{similarLot.saleDate.split(',')[0]}</span>
                </div>
                <p className="text-sm font-medium truncate mb-1">{similarLot.title}</p>
                <Link href={`/app/artists/${similarLot.artistSlug}`} className="text-xs text-accent-primary hover:underline">
                  {similarLot.artist}
                </Link>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-lg font-display font-light">{formatCurrency(similarLot.hammer)}</span>
                  <span className={`text-xs font-medium tabular-nums ${similarLot.hammer > similarLot.estimateHigh ? 'text-positive' : 'text-text-secondary'}`}>
                    {similarLot.hammer > similarLot.estimateHigh ? `+${((similarLot.hammer - similarLot.estimateHigh) / similarLot.estimateHigh * 100).toFixed(0)}%` : 'Within est.'}
                  </span>
                </div>
              </Link>
            ))}
        </div>
        {Object.values(lotsData).filter(l => l.id !== lot.id && l.movement === lot.movement).length === 0 && (
          <p className="text-sm text-text-tertiary text-center py-4">No similar lots found in current data.</p>
        )}
      </div>

      {/* More by Artist */}
      <div className="card p-6 mb-6">
        <h2 className="font-medium mb-4 flex items-center gap-2">
          <svg className="w-5 h-5 text-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          More by{' '}
          <Link href={`/app/artists/${lot.artistSlug}`} className="text-accent-primary hover:underline">
            {lot.artist}
          </Link>
        </h2>
        <div className="grid md:grid-cols-3 gap-4">
          {Object.values(lotsData)
            .filter(l => l.id !== lot.id && l.artistSlug === lot.artistSlug)
            .slice(0, 3)
            .map((artistLot) => (
              <Link
                key={artistLot.id}
                href={`/app/lots/${artistLot.id}`}
                className="p-4 rounded-xl bg-page hover:bg-card transition-colors"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-text-tertiary">{artistLot.house}</span>
                  <span className="text-xs text-text-tertiary">{artistLot.year}</span>
                </div>
                <p className="text-sm font-medium truncate mb-1">{artistLot.title}</p>
                <p className="text-xs text-text-tertiary">{artistLot.medium.split(',')[0]}</p>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-lg font-display font-light">{formatCurrency(artistLot.hammer)}</span>
                </div>
              </Link>
            ))}
        </div>
        {Object.values(lotsData).filter(l => l.id !== lot.id && l.artistSlug === lot.artistSlug).length === 0 && (
          <p className="text-sm text-text-tertiary text-center py-4">
            No other lots by this artist in current data.{' '}
            <Link href={`/app/artists/${lot.artistSlug}`} className="text-accent-primary hover:underline">
              View artist profile →
            </Link>
          </p>
        )}
      </div>

      {/* Export button */}
      <div className="flex justify-end">
        <TierGate feature="export" showPrompt={false}>
          <button
            className="pill text-sm flex items-center gap-2"
            style={{ background: 'transparent', border: '1px solid var(--border-standard)', color: 'var(--text-primary)' }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export PDF
          </button>
        </TierGate>
      </div>
    </div>
  )
}
